import { useApolloClient, useMutation } from '@apollo/client';
import { getInitialPckValues } from '@openx/components-package/components/PackageForm/constants';
import { getDiff } from '@openx/components-package/components/PackageForm/utils/formStateConverter';
import { determineFormType, getNewPath } from '@openx/components-package/utils/formNavigation';
import { useStandaloneContextualSegmentsEnabled } from '@openx/components-targeting/Targeting/ContextualSegments/featureFlags';
import type { DealFormDataWithTargeting } from '@openx/types/deal';
import type { UpdatePackageFormParam, UseUpdateForm } from '@openx/types/package/PackageHookTypes';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { UpdatePackageDocument } from '../../../graphql/mutation/packages/UpdatePackage.generated';
import { useLocalErrorHandling } from '../../../modules/graphql/useLocalErrorHandling';
import { trackContextualSegmentsSaved } from '../../targeting/utils/trackContextualSegmentsSaved';
import { transformTargetingForMutation, withContextualSegmentForUpdate } from './transformTargetingForMutation';

export const useUpdateForm: UseUpdateForm = pckgUid => {
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const historyState = location.state;
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const isStandaloneContextualSegments = useStandaloneContextualSegmentsEnabled();

  useLocalErrorHandling(UpdatePackageDocument, () => true);

  const [updatePackageMutation, { loading }] = useMutation(UpdatePackageDocument);
  const initialPckgValues = useMemo(() => getInitialPckValues(), []);

  const updatePackage = ({
    diff,
    values,
    initialValues,
    setSubmitErrors,
    clearCache = () => {
      noop();
    },
  }: UpdatePackageFormParam) => {
    const diffFromInitial = getDiff(values, initialPckgValues);
    const targeting = diffFromInitial.targeting
      ? diffFromInitial.targeting
      : {
          inter_dimension_operator: Criteria.ALL,
          metacategory: initialPckgValues.targeting.metacategory,
        };

    const url_targeting = diffFromInitial.url_targeting ? diffFromInitial.url_targeting : undefined;

    const urls = diff.urls ? (diff.urls as string[]) : undefined;

    const mutationTargeting = transformTargetingForMutation(targeting);
    const diffWithFullTargeting = {
      ...diff,
      targeting: mutationTargeting && withContextualSegmentForUpdate(mutationTargeting),
      url_targeting,
      urls,
    };

    updatePackageMutation({
      onCompleted: data => {
        setSubmitErrors?.([]);
        clearCache();
        apolloClient.cache.evict({ fieldName: 'packages', id: 'ROOT_QUERY' });
        apolloClient.cache.evict({ fieldName: 'packageById', id: 'ROOT_QUERY' });
        apolloClient.cache.evict({ fieldName: 'openxCuratedPackages', id: 'ROOT_QUERY' });
        const type = determineFormType(historyState);

        trackContextualSegmentsSaved({
          entity: 'package',
          isEdit: true,
          isStandaloneContextualSegments,
          targeting: values.targeting,
          initialTargeting: initialValues.targeting,
          fromDealForm: type === 'deal',
        });

        const packageName = data.modifyPackage.name;

        enqueueSnackbar(
          t('Package "{packageName}" was successfully updated', {
            packageName,
          }),
          { variant: 'success' }
        );

        if (type === 'deal' && values) {
          const nextDeal = produce<DealFormDataWithTargeting>(historyState.deal, draft => {
            draft.targeting = cloneDeep(values);
          });
          const nextDealState = { deal: nextDeal };
          const newPath = getNewPath(nextDealState, type);
          navigate(newPath, { replace: true, state: nextDealState });
        } else if (type) {
          const newPath = getNewPath({ [type]: { ...historyState[type] } }, type);
          navigate(newPath, { replace: true, state: historyState });
        } else {
          navigate(`/inventory/packages/${pckgUid}/details`);
        }
      },
      onError: error => {
        const errorMessages = error.graphQLErrors.reduce((_prev: string[], curr) => {
          const firstErrorMessage = curr?.extensions?.errors?.[0]?.message;
          return firstErrorMessage ? [firstErrorMessage] : [];
        }, []);

        enqueueSnackbar(t('Failed to update package'), { variant: 'error' });

        setSubmitErrors?.(errorMessages);
      },
      variables: { id: pckgUid, package: diffWithFullTargeting },
    });
  };

  return { isUpdating: loading, updatePackage };
};
